/**
 * Returns a curried function of the provided function, so that:
 *
 * f(a, b, c) = f(a, b)(c) = f(a)(b)(c) = f(a)(b, c)
 *
 * @param {Function} f
 * @param {..*} Initial parameters
 * @return {Function} The curried function
 */
const curry = (f, ...args) => (args.length >= f.length ? f(...args) : curry.bind(this, f, ...args))

/**
 * Functional wrapper for array map function.
 *
 * @param {Function} f
 * @param {*} arr
 */
const map = curry((f, arr) => (Array.isArray(arr) ? arr.map(f) : f(arr)))

/**
 * Partial application
 * @param {Function} f
 * @param {..*} args Initial parameters
 */
const partial = (f, ...args) => f.bind(this, ...args)

/**
 * Transform a value by chaining a list of function from left to right
 *
 * @param val
 * @param {..Function} funcs
 * @return {*}
 */
const pipe = (...funcs) =>
  function (val) {
    return funcs.reduce((acc, f) => f.apply(this, [acc]), val)
  }

/**
 * Get property of an object.
 *
 * This is a curried function.
 *
 * @param {string} prop
 * @param {Object} obj
 * @return {*}
 */
const getProp = curry((prop, obj) => {
  return obj[prop]
})

const setProp = curry((prop, value, obj) => {
  obj[prop] = value

  return obj
})

/**
 * Delay execution of callback used on individual items of a list.
 *
 * This function is curried.
 *
 * @param {number} initial Initial timeout
 * @param {number} step Delay per execution
 * @param {Function} callback Callback
 * @param {Array} items
 */

const whileDo = (pred, fn, initial) => (pred(initial) ? whileDo(pred, fn, fn(initial)) : initial)

/**
 * Get the nth element of an array
 *
 * @param {number}
 * @param {Array}
 * @return {*}
 */
const nth = curry((index, array) => (index < 0 ? array[array.length + index] : array[index]))

/**
 * Get the nth element of an array
 *
 * @param {Array}
 * @return {*}
 */
const first = nth(1)

const last = nth(-1)
const add = curry((b, a) => a + b)

/**
 * Create an array out of an array-like object
 *
 * @param {Object} Array-like object
 * @return {Array} Array
 */
const makeArray = (arrayLike) => Array.prototype.slice.call(arrayLike)

const hasOwnProperties = (property, obj) => {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

const inViewPort = (el, callback) => {
  const rect = el.getBoundingClientRect()

  rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
    typeof callback === 'function' &&
    callback()
}

const handleize = (string) =>
  string
    .toLowerCase()
    .replace(/[^\w\u00C0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const isEmpty = (val) => val == null || !(Object.keys(val) || val).length

const formatName = (string) =>
  string && typeof string === 'string' && string.includes('-') ? string.split('-')[1] : string

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Uses the DOMParser API to parse the encoded string as an HTML document.
 * Accesses the textContent of the <body> to retrieve the decoded text.
 *
 * Avoids creating additional DOM elements.
 * More efficient for frequent calls as it leverages native browser APIs designed for parsing HTML.
 *
 * @param {string} string
 * @return
 */
const decodeHtml = (string) => new DOMParser().parseFromString(string, 'text/html').body.textContent

const formatLabelFilter = (string) => {
  if (!string || typeof string !== 'string') {
    return
  }

  if (handleize(string) === 'made-to-order') {
    return translate.collection.madeToOrder
  }

  if (handleize(string) === 'ready-to-ship') {
    return translate.collection.readyToShip
  }

  return string
}

const fetchProductByAjax = (handles) => {
  const allFetchs = handles.map((handle) => {
    return fetch(`${window.Shopify.routes.root}products/${handle}?view=ajax`)
      .then((response) => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json()
        } else {
          throw Error(response.statusText)
        }
      })
      .then((product) => {
        return product
      })
  })

  return Promise.allSettled(allFetchs).then((results) =>
    results.filter((result) => result.status === 'fulfilled').map((results) => results.value)
  )
}

export {
  add,
  makeArray,
  curry,
  first,
  getProp,
  last,
  map,
  nth,
  partial,
  pipe,
  setProp,
  whileDo,
  hasOwnProperties,
  inViewPort,
  isEmpty,
  formatName,
  delay,
  handleize,
  decodeHtml,
  formatLabelFilter,
  fetchProductByAjax
}
